//
// Copyright 2025 DXOS.org
//

import * as Context from 'effect/Context';
import * as Duration from 'effect/Duration';
import * as Effect from 'effect/Effect';
import * as Layer from 'effect/Layer';
import * as Schedule from 'effect/Schedule';
import type * as HttpClient from 'effect/unstable/http/HttpClient';
import type * as HttpClientError from 'effect/unstable/http/HttpClientError';
import type * as HttpClientResponse from 'effect/unstable/http/HttpClientResponse';

import { log } from '@dxos/log';
import { EdgeCredentialsHeaderCodec } from '@dxos/protocols';

// TODO(burdon): Factor out.

export type RetryOptions = {
  timeout: Duration.Duration;
  retryTimes: number;
  retryBaseDelay: Duration.Duration;
};

// Layer pattern.
export class HttpConfig extends Context.Service<HttpConfig, RetryOptions>()('HttpConfig') {
  static default = Layer.succeed(HttpConfig, {
    timeout: Duration.millis(1_000),
    retryTimes: 3,
    retryBaseDelay: Duration.millis(1_000),
  });
}

// HOC pattern.
export const withRetry = (
  effect: Effect.Effect<HttpClientResponse.HttpClientResponse, HttpClientError.HttpClientError, HttpClient.HttpClient>,
  {
    timeout = Duration.millis(1_000),
    retryBaseDelay = Duration.millis(1_000),
    retryTimes = 3,
  }: Partial<RetryOptions> = {},
) => {
  return effect.pipe(
    Effect.flatMap((res) =>
      // Treat 500 errors as retryable?
      res.status === 500 ? Effect.fail(new Error(res.status.toString())) : res.json,
    ),
    Effect.timeout(timeout),
    Effect.retry({
      schedule: Schedule.exponential(retryBaseDelay).pipe(Schedule.jittered),
      times: retryTimes,
    }),
  );
};

export const withRetryConfig = (
  effect: Effect.Effect<HttpClientResponse.HttpClientResponse, HttpClientError.HttpClientError, HttpClient.HttpClient>,
) =>
  Effect.gen(function* () {
    const config = yield* HttpConfig;
    return yield* withRetry(effect, config);
  });

export const withLogging = <A extends HttpClientResponse.HttpClientResponse, E, R>(effect: Effect.Effect<A, E, R>) =>
  effect.pipe(
    // v4's `tap` requires an Effect; a bare side effect has to be lifted.
    Effect.tap((res) =>
      Effect.sync(() => {
        log.info('response', { status: res.status });
      }),
    ),
  );

/**
 * Frame an encoded verifiable presentation as an `Authorization` header value.
 * Kept as a named export for existing callers; the wire format itself lives in
 * {@link EdgeCredentialsHeaderCodec} so the client and the Edge worker share one definition.
 */
export const encodeAuthHeader = (presentation: Uint8Array): string => EdgeCredentialsHeaderCodec.encode(presentation);
